import * as React from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form } from '@/components/ui/form';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { SingleSelect } from '@/modules/common';
import { ExternalProject, ProjectIntegration } from '@/modules/projects/types/project-integration';

interface ProjectIntegrationsSelectCellProps {
  integration: ProjectIntegration;
  onProjectChange: (integrationId: string, projectId?: string) => void;
  isReadMode: boolean;
  t: ReturnType<typeof useTranslation>['t'];
}

const NOT_SELECTED_VALUE = '__not_selected__';

export const ProjectIntegrationsSelectCell: React.FC<ProjectIntegrationsSelectCellProps> = ({
  integration,
  onProjectChange,
  isReadMode,
  t,
}) => {
  const form = useForm({
    defaultValues: {
      selectedProjectId: integration.selectedValue?.id || NOT_SELECTED_VALUE,
    },
  });

  // Watch form changes
  const selectedProjectId = form.watch('selectedProjectId');

  // Update form when integration changes externally (from parent state updates)
  React.useEffect(() => {
    form.setValue('selectedProjectId', integration.selectedValue?.id || NOT_SELECTED_VALUE);
  }, [integration.selectedValue?.id, form]);

  // Trigger callback when user makes a selection
  React.useEffect(() => {
    // Only trigger if the current form value differs from the integration's value
    // This means user made a change that hasn't been reflected in parent state yet
    const currentValue = integration.selectedValue?.id || NOT_SELECTED_VALUE;
    if (selectedProjectId !== currentValue) {
      // Convert special "not selected" value to undefined
      const projectId = selectedProjectId === NOT_SELECTED_VALUE ? undefined : selectedProjectId;
      onProjectChange(integration.id, projectId);
    }
  }, [selectedProjectId, integration.id, integration.selectedValue?.id, onProjectChange]);

  // Build options with "Not selected" as first option
  const projectOptions = React.useMemo(() => {
    const notSelectedOption = {
      value: NOT_SELECTED_VALUE,
      label: t('details.tabs.integrations.notSelected'),
    };

    const mappedProjects = (integration.projects || []).map((project: ExternalProject) => ({
      value: project.id,
      label: project.name,
    }));

    return [notSelectedOption, ...mappedProjects];
  }, [integration.projects, t]);

  const isDisabled = isReadMode || (integration.projects?.length || 0) === 0;

  const selectElement = (
    <div className="w-full max-w-xs">
      <Form {...form}>
        <SingleSelect
          name="selectedProjectId"
          control={form.control}
          placeholder={
            integration.allProjectsLinked
              ? t('details.tabs.integrations.notSelected')
              : (integration.projects?.length || 0) === 0
                ? t('details.tabs.integrations.noProjectsAvailable')
                : t('details.tabs.integrations.selectProject')
          }
          options={projectOptions}
          disabled={isDisabled}
          searchable={true}
          showDescriptionInOptions={false}
          showDescriptionInSelector={false}
        />
      </Form>
    </div>
  );

  if (integration.allProjectsLinked) {
    return (
      <SimpleTooltip
        content={t('details.tabs.integrations.allProjectsLinkedTooltip', {
          integrationName: integration.type,
        })}
      >
        {selectElement}
      </SimpleTooltip>
    );
  }

  return selectElement;
};
